import React from "react";

import { LgStatusIcon } from "manager/components/app-summary/AppListV2/StatusIcon";
import { SmallText } from "shared/components/Typography";
import type {
  Vero1ChecklistItem,
  Vero1Severity,
} from "shared/interfaces/api/vero1";
import { Vero1RowKind } from "shared/interfaces/api/vero1";

import { formatReviewTime } from "../vero1Dates";

import { AUTOSAVE_STRINGS } from "./autosaveStrings";
import { reasonLabel } from "./reasonCodes";
import {
  NO_SCORE_LABEL,
  NO_VERDICT_LABEL,
  clientNameFor,
  displayName,
  effectiveTone,
  formatItemValue,
  getRecords,
  isAnnotationOnlyItem,
  isScoreCriterion,
  lumenLabel,
  lumenTone,
  parseValue,
  severityLabel,
} from "./formatItemValue";
import {
  CircleAlertGlyph,
  SpinnerGlyph,
  LockGlyph,
  PencilGlyph,
  TrashGlyph,
} from "./icons";
import {
  hasResultMoved,
  isAgentTouched,
  isLeasingTouched,
  needsFindings as rowNeedsFindings,
  needsName as rowNeedsName,
  needsReason as rowNeedsReason,
} from "./itemState";
import {
  Btnk,
  ConflictCopy,
  ConflictFrame,
  ConflictHeading,
  ConflictStrip,
  ConflictStripActions,
  SavingLine,
} from "./reportStyled";
import {
  AgentBlock,
  AttributionLine,
  IncompleteMarker,
  ApplicantLine,
  ChangedChip,
  BeforeAfterPill,
  ChangeStrip,
  EditAction,
  RowActions,
  RemoveAction,
  RemoveConfirm,
  RemoveConfirmNo,
  RemoveConfirmYes,
  EditingTag,
  FieldLabel,
  LabeledLine,
  ModifiedPip,
  PipSlot,
  AnnotationOnlyChip,
  ReasonChip,
  RecordList,
  ResultWord,
  RowBody,
  RowContainer,
  RowHeader,
  RowLabel,
  RowTitle,
  RowValue,
  UnassessedMarker,
} from "./rowStyled";

export const rowFactValue = (item: Vero1ChecklistItem): unknown => {
  if (item.rowKind === Vero1RowKind.Sentilink) return null;
  const value = parseValue(item.originalValue);
  if (isScoreCriterion(item.criterionKey)) return value ?? NO_SCORE_LABEL;
  if (typeof value === "number" || Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim() !== "") return value;
  return null;
};

const isAnnotationOnlyRow = (item: Vero1ChecklistItem) =>
  item.rowKind === Vero1RowKind.Sentilink || isAnnotationOnlyItem(item);

/** Annotation-only rows carry structured "Label: value" lines; the label gets
 *  a medium weight. Other rows keep Lumen's prose untouched. */
const AnnotationExplanation = ({
  text,
  weak,
}: {
  text: string;
  weak?: boolean;
}) => (
  <SmallText weak={weak} style={{ whiteSpace: "pre-line" }}>
    {text.split("\n").map((line, i) => {
      const m = line.match(/^([^:]+):\s?(.*)$/);
      return (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={i}>
          {i > 0 && "\n"}
          {m ? (
            <>
              <span style={{ fontWeight: 500 }}>{m[1]}:</span> {m[2]}
            </>
          ) : (
            line
          )}
        </React.Fragment>
      );
    })}
  </SmallText>
);

export interface ChecklistRowProps {
  item: Vero1ChecklistItem;
  onEdit?: (item: Vero1ChecklistItem) => void;
  /** Present only for agent-added custom rows on a live draft &mdash;
   *  removes the row the analyst added by mistake. */
  onRemove?: (item: Vero1ChecklistItem) => void;
  /** The inline "remove? confirm / cancel" step is showing for this row. */
  removing?: boolean;
  removeBusy?: boolean;
  onConfirmRemove?: () => void;
  onCancelRemove?: () => void;
  readOnly?: boolean;
  /** Set when the checklist spans 2+ applications: per-applicant labels
   *  repeat, so each row names its applicant. */
  showApplicant?: boolean;
  /** The row's editor is open (spec §09): the pencil becomes an EDITING tag,
   *  the row tints, and the editor card renders as children under Lumen's
   *  reasoning — everything the editor already holds is hidden here. */
  editing?: boolean;
  /** The open editor's save was rejected — the tag reads NOT SAVED (§9.4). */
  editConflict?: boolean;
  /** A background flush 409ed after the editor collapsed (§9.4). */
  saveConflict?: boolean;
  /** A retry/flush is in flight for this collapsed row — keep an in-progress
   *  strip visible so the failed banner doesn't blink out to nothing. */
  saveRetrying?: boolean;
  /** A background flush exhausted its retries; the buffer is kept. */
  saveFailed?: boolean;
  conflictRefreshing?: boolean;
  onRetryFlush?: () => void;
  severityRefused?: Vero1Severity[];
  nameRefused?: string[];
  children?: React.ReactNode;
}

const listNames = (names: string[]): string =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;

export const ChecklistRow = ({
  item,
  onEdit,
  onRemove,
  removing,
  removeBusy,
  onConfirmRemove,
  onCancelRemove,
  readOnly,
  showApplicant,
  editing,
  editConflict,
  saveConflict,
  saveFailed,
  saveRetrying,
  conflictRefreshing,
  onRetryFlush,
  severityRefused,
  nameRefused,
  children,
}: ChecklistRowProps) => {
  const factValue = rowFactValue(item);
  const records = Array.isArray(factValue);
  const recordLines = records ? getRecords(item.originalValue) : [];
  const annotationOnly = isAnnotationOnlyRow(item);

  // the server's flag also knows a same-severity rename is a moved result;
  // hasResultMoved covers payloads that predate the flag
  const moved = item.resultMoved ?? hasResultMoved(item);
  // the stored pick is what leasing reads, ahead of the configured default
  const effectiveName =
    displayName(item.overrideOutcomeName) ||
    clientNameFor(item.overrideSeverity, item);
  const markerTone = effectiveTone(item);
  const lumen = lumenTone(item);
  const noVerdict = lumen === null && item.rowKind === Vero1RowKind.Outcome;

  const agentFindings = item.overrideExplanation;
  const lumenExplanation = item.originalExplanation;

  const editSummary = moved ? "Result changed" : "Findings revised";

  // leasing-visible touches only — a note is internal annotation and must
  // not read as a result attestation
  const leasingTouched = isLeasingTouched(item);
  // a note-only save must still render — the locked note line is the only
  // place the note surfaces outside the editor
  const agentTouched = isAgentTouched(item);

  // findings are required by report time — an edited row without them carries
  // the amber marker and holds Continue to report (settled validation model)
  const needsFindings = rowNeedsFindings(item);
  const needsReason = rowNeedsReason(item);
  const needsName = rowNeedsName(item);

  const showDiffPill =
    !annotationOnly && leasingTouched && (moved || lumen !== null);

  const editLabel = annotationOnly
    ? "Annotate this result"
    : item.rowKind === Vero1RowKind.Fact
    ? "Add findings"
    : markerTone === null
    ? "Assess this item"
    : "Edit result";

  const explanation = (weak?: boolean) => {
    if (noVerdict) {
      return (
        <SmallText
          weak={weak}
          data-testid="vero1-no-verdict"
          style={{ whiteSpace: "pre-line" }}
        >
          {lumenExplanation
            ? `${NO_VERDICT_LABEL} — ${lumenExplanation}`
            : NO_VERDICT_LABEL}
        </SmallText>
      );
    }
    if (!lumenExplanation) return null;
    return annotationOnly ? (
      <AnnotationExplanation text={lumenExplanation} weak={weak} />
    ) : (
      <SmallText weak={weak} style={{ whiteSpace: "pre-line" }}>
        {lumenExplanation}
      </SmallText>
    );
  };

  return (
    <RowContainer
      $editing={editing}
      $record={Boolean(readOnly)}
      data-testid="vero1-checklist-row"
      data-vero1-item={item.id}
    >
      <RowHeader>
        {/* the "touched" pip leads the marker row (spec) — a fixed slot so an
            untouched row's marker still aligns with a touched one's */}
        <PipSlot>
          {agentTouched && <ModifiedPip data-testid="vero1-modified-pip" />}
        </PipSlot>
        {markerTone === null ? (
          // dashed neutral marker, never red — the row is part of the
          // assessment and awaiting the agent, not broken or failing
          <UnassessedMarker data-testid="vero1-unassessed-marker" />
        ) : (
          <LgStatusIcon
            status={markerTone}
            size={22}
            testId={`vero1-check-state-${markerTone}`}
          />
        )}
        <RowTitle $confirming={removing}>
          <RowLabel>{item.label}</RowLabel>
          {factValue !== null && (
            <RowValue data-testid="vero1-row-value">
              {formatItemValue(item.originalValue, item.criterionKey)}
            </RowValue>
          )}
        </RowTitle>
        {!editing && !readOnly && item.changedVsPriorRun && (
          <ChangedChip
            data-testid="vero1-changed-chip"
            title="Changed since your last review"
            aria-label="Changed since your last review"
          >
            New result
          </ChangedChip>
        )}
        {editing ? (
          <EditingTag $conflict={editConflict} data-testid="vero1-editing-tag">
            {editConflict ? "Not saved" : "Editing"}
          </EditingTag>
        ) : removing ? (
          <RemoveConfirm data-testid="vero1-remove-confirm">
            <RemoveConfirmYes
              type="button"
              data-testid="vero1-remove-confirm-yes"
              disabled={removeBusy}
              onClick={onConfirmRemove}
            >
              {removeBusy ? "Removing…" : "Remove"}
            </RemoveConfirmYes>
            <RemoveConfirmNo
              type="button"
              disabled={removeBusy}
              onClick={onCancelRemove}
            >
              Cancel
            </RemoveConfirmNo>
          </RemoveConfirm>
        ) : (
          !readOnly &&
          (onEdit || onRemove) && (
            <RowActions>
              {onEdit && (
                <EditAction
                  type="button"
                  aria-label={`Edit ${item.label}`}
                  title={editLabel}
                  onClick={() => onEdit(item)}
                >
                  <PencilGlyph />
                </EditAction>
              )}
              {onRemove && (
                <RemoveAction
                  type="button"
                  data-testid="vero1-remove-item"
                  aria-label={`Remove ${item.label}`}
                  title="Remove this added item"
                  onClick={() => onRemove(item)}
                >
                  <TrashGlyph />
                </RemoveAction>
              )}
            </RowActions>
          )
        )}
      </RowHeader>

      {showApplicant && item.applicantName && (
        <ApplicantLine data-testid="vero1-applicant-tag">
          {item.applicantName}
        </ApplicantLine>
      )}

      {severityRefused && severityRefused.length > 0 && (
        <ConflictFrame $neutral data-testid="vero1-row-severity-refused">
          <ConflictStrip $neutral>
            <CircleAlertGlyph />
            <div>
              <ConflictHeading $neutral>
                {AUTOSAVE_STRINGS.severityRefusedHeading}
              </ConflictHeading>
              <ConflictCopy>
                {AUTOSAVE_STRINGS.severityRefusedBody(
                  listNames(
                    severityRefused.map(
                      (severity) =>
                        clientNameFor(severity, item) ?? severityLabel(severity)
                    )
                  )
                )}
              </ConflictCopy>
            </div>
          </ConflictStrip>
        </ConflictFrame>
      )}
      {nameRefused && nameRefused.length > 0 && (
        <ConflictFrame $neutral data-testid="vero1-row-name-refused">
          <ConflictStrip $neutral>
            <CircleAlertGlyph />
            <div>
              <ConflictHeading $neutral>
                {AUTOSAVE_STRINGS.nameRefusedHeading}
              </ConflictHeading>
              <ConflictCopy>
                {AUTOSAVE_STRINGS.nameRefusedBody(listNames(nameRefused))}
              </ConflictCopy>
            </div>
          </ConflictStrip>
        </ConflictFrame>
      )}
      {!editing && saveRetrying && (
        <SavingLine data-testid="vero1-row-save-retrying">
          <SpinnerGlyph size={12} />
          {AUTOSAVE_STRINGS.saving}
        </SavingLine>
      )}
      {!editing && !saveRetrying && saveConflict && (
        <ConflictFrame data-testid="vero1-row-save-conflict">
          <ConflictStrip>
            <CircleAlertGlyph />
            <div>
              <ConflictHeading>
                {AUTOSAVE_STRINGS.rowConflictHeading}
              </ConflictHeading>
              <ConflictCopy>{AUTOSAVE_STRINGS.rowConflictBody}</ConflictCopy>
              <ConflictStripActions>
                <Btnk
                  type="button"
                  $variant="accent"
                  $height={34}
                  data-testid="vero1-row-retry-flush"
                  disabled={conflictRefreshing}
                  onClick={onRetryFlush}
                >
                  {AUTOSAVE_STRINGS.tryAgain}
                </Btnk>
              </ConflictStripActions>
            </div>
          </ConflictStrip>
        </ConflictFrame>
      )}
      {!editing && !saveRetrying && !saveConflict && saveFailed && (
        <ConflictFrame data-testid="vero1-row-save-failed">
          <ConflictStrip>
            <CircleAlertGlyph />
            <div>
              <ConflictHeading>
                {AUTOSAVE_STRINGS.failedHeading}
              </ConflictHeading>
              <ConflictCopy>{AUTOSAVE_STRINGS.failedBody}</ConflictCopy>
              <ConflictStripActions>
                <Btnk
                  type="button"
                  $variant="accent"
                  $height={34}
                  data-testid="vero1-row-retry-flush"
                  onClick={onRetryFlush}
                >
                  {AUTOSAVE_STRINGS.tryAgain}
                </Btnk>
              </ConflictStripActions>
            </div>
          </ConflictStrip>
        </ConflictFrame>
      )}

      {/* spec §09: the open editor holds every agent-authored value itself, so
          the row shows only Lumen's reasoning above the card. The card sits
          outside RowBody — it spans the full row, free of the 30px indent. */}
      {editing ? (
        <>
          {(noVerdict || lumenExplanation) && (
            <RowBody>{explanation(true)}</RowBody>
          )}
          {children}
        </>
      ) : (
        <RowBody>
          {/* Lumen's full reasoning on every row — no truncation (spec §03),
            below the title as on every other row. */}
          {explanation()}
          {/* records criteria (eng-gap): the count is inline in the title
            above; the individual records are the evidence, flush beneath the
            reasoning. Raw objects would render as [object Object] otherwise. */}
          {records && recordLines.length > 0 && (
            <RecordList>
              {recordLines.map((line, index) => (
                // records have no stable id; order is the only key available
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>
                  <SmallText>{line}</SmallText>
                </li>
              ))}
            </RecordList>
          )}

          {needsName && (
            <IncompleteMarker data-testid="vero1-needs-name">
              Needs a name
            </IncompleteMarker>
          )}
          {needsReason && (
            <IncompleteMarker data-testid="vero1-needs-reason">
              Needs a reason
            </IncompleteMarker>
          )}
          {needsFindings && (
            <IncompleteMarker data-testid="vero1-needs-findings">
              Needs findings
            </IncompleteMarker>
          )}
          {agentTouched && (
            <AgentBlock data-testid="vero1-agent-block">
              {(showDiffPill || item.reasonCode || annotationOnly) && (
                <ChangeStrip>
                  {annotationOnly ? (
                    <AnnotationOnlyChip data-testid="vero1-annotation-only-chip">
                      Annotation only
                    </AnnotationOnlyChip>
                  ) : (
                    showDiffPill && (
                      <BeforeAfterPill data-testid="vero1-before-after">
                        {/* only a moved result is struck; a struck unchanged verdict reads as revised */}
                        <ResultWord
                          $tone={lumen}
                          $neutral={lumen == null}
                          $struck={moved}
                        >
                          {lumenLabel(item)}
                        </ResultWord>
                        <span>&nbsp;→&nbsp;</span>
                        {moved ? (
                          <ResultWord $tone={markerTone}>
                            {effectiveName}
                          </ResultWord>
                        ) : (
                          <span>Unchanged</span>
                        )}
                      </BeforeAfterPill>
                    )
                  )}
                  {item.reasonCode && (
                    <ReasonChip $fail={markerTone === "fail"}>
                      {item.otherReasonLabel
                        ? `${reasonLabel(item.reasonCode)} · ${
                            item.otherReasonLabel
                          }`
                        : reasonLabel(item.reasonCode)}
                    </ReasonChip>
                  )}
                </ChangeStrip>
              )}
              {agentFindings && (
                <LabeledLine>
                  <FieldLabel>Findings</FieldLabel>
                  <SmallText>{agentFindings}</SmallText>
                </LabeledLine>
              )}
              {item.lastEditedAt && (
                <AttributionLine data-testid="vero1-attribution">
                  {editSummary}
                  {item.lastEditedByName ? (
                    <>
                      {" by "}
                      <b>{item.lastEditedByName}</b>
                    </>
                  ) : (
                    ""
                  )}
                  {` · ${formatReviewTime(item.lastEditedAt)}`}
                </AttributionLine>
              )}
              {item.actionForLeasing && (
                <LabeledLine>
                  <FieldLabel>Leasing action</FieldLabel>
                  <SmallText>{item.actionForLeasing}</SmallText>
                </LabeledLine>
              )}
              {/* the panel is agent-internal; the client serializers are what
                keep this off client surfaces */}
              {item.internalNote && (
                <LabeledLine data-testid="vero1-internal-note-line">
                  <FieldLabel>
                    <LockGlyph /> Internal note
                  </FieldLabel>
                  <SmallText>{item.internalNote}</SmallText>
                </LabeledLine>
              )}
            </AgentBlock>
          )}
        </RowBody>
      )}
    </RowContainer>
  );
};
